import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output,
    ChangeDetectorRef,
    computed,
    OnInit,
} from "@angular/core";
import { S25ReservationUtil } from "../../../utils/s25.reservation.util";
import { Table } from "../../../../s25-table/Table";
import { S25EventOccurrencesService } from "../../s25.event.occurrences.service";
import { S25Reservation } from "../../../ReservationI";
import { Event } from "../../../../../pojo/Event";
import { WaitingEntry, WaitlistEntry } from "../../../../../pojo/Waitlist";
@Component({
    selector: "occurrence-details",
    template: `@if (init) {
        @if (eventState === 99 || eventState === 98) {
            <div>This occurrence has been cancelled</div>
        }
        <div class="occ-expand-all-details" (click)="showDetails($event, 'autoClick')"></div>
        <div (click)="showDetails($event, 'manualClick')" class="details-wrapper">
            @if (eventState !== 99 && eventState !== 98) {
                <div>
                    @if (occurrence()?.locations?.length) {
                        @let firstLocation = occurrence()!.locations![0];
                        <div>
                            @if (firstLocation.reserved?.length) {
                                <div>
                                    <span class="s25-item-type-icon-holder-spaces">
                                        <span class="icon-space s25item-type-icon"></span>
                                    </span>
                                    {{
                                        firstLocation.reserved!.length > 1
                                            ? (firstLocation?.reserved)!.length + " locations"
                                            : firstLocation.reserved![0].itemName || "1 location"
                                    }}
                                </div>
                            }
                            @if (firstLocation.draft?.length && eventState === 0) {
                                <div>
                                    <span class="s25-item-type-icon-holder-spaces">
                                        <span class="icon-space-pending s25item-type-icon"></span>
                                    </span>
                                    {{
                                        firstLocation.draft!.length > 1
                                            ? firstLocation.draft!.length + " locations"
                                            : firstLocation.draft![0].itemName || "1 location"
                                    }}
                                </div>
                            }
                            @if (firstLocation.requested?.length) {
                                <div>
                                    <span class="s25-item-type-icon-holder-spaces">
                                        <span class="icon-space-pending s25item-type-icon"></span>
                                    </span>
                                    {{
                                        firstLocation.requested!.length > 1
                                            ? firstLocation.requested!.length + " locations awaiting approval"
                                            : firstLocation.requested![0].itemName ||
                                              "1 location" + " is awaiting approval"
                                    }}
                                </div>
                            }

                            @if (eventWaiting() > 0) {
                                <div>
                                    <span class="s25-item-type-icon-holder-spaces">
                                        <span class="icon-event-pending s25item-type-icon"></span>
                                    </span>

                                    ({{ eventWaiting() > 1 ? eventWaiting() + " events waiting" : "1 event waiting" }})
                                </div>
                            }
                        </div>
                    }
                    @if (locationWaiting() > 0) {
                        <div>
                            <span class="s25-item-type-icon-holder-spaces">
                                <span class="icon-space-pending s25item-type-icon"></span>
                            </span>

                            ({{
                                locationWaiting() > 1 ? locationWaiting() + " locations waiting" : "1 location waiting"
                            }})
                        </div>
                    }
                    @if (occurrence()?.resources?.length) {
                        @let firstResource = occurrence()!.resources![0];
                        <div>
                            @if (firstResource.reserved?.length) {
                                <div>
                                    <span class="s25-item-type-icon-holder-spaces">
                                        <span class="icon-resource s25item-type-icon"></span>
                                    </span>
                                    {{
                                        firstResource.reserved!.length > 1
                                            ? firstResource.reserved!.length + " resources"
                                            : firstResource.reserved![0].itemName || "1 resource"
                                    }}
                                </div>
                            }
                            @if (firstResource.draft?.length && eventState === 0) {
                                <div>
                                    <span class="s25-item-type-icon-holder-resources">
                                        <span class="icon-resource-pending s25item-type-icon"></span>
                                    </span>
                                    {{
                                        firstResource.draft!.length > 1
                                            ? firstResource.draft!.length + " resources"
                                            : firstResource.draft![0]?.itemName || "1 resource"
                                    }}
                                </div>
                            }
                            @if (firstResource.requested?.length) {
                                <div>
                                    <span class="s25-item-type-icon-holder-resources">
                                        <span class="icon-resource-pending s25item-type-icon"></span>
                                    </span>

                                    {{
                                        firstResource.requested!.length > 1
                                            ? firstResource.requested!.length + " resources awaiting approval"
                                            : firstResource.requested![0]?.itemName ||
                                              "1 resource" + " is awaiting approval"
                                    }}
                                </div>
                            }
                        </div>
                    }
                </div>
            }
        </div>
    }`,
    styles: [
        `
            .details-wrapper:hover {
                cursor: pointer;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class OccurrenceDetailsComponent implements OnInit {
    @Input() occ: S25Reservation;
    @Input() eventState?: Event.State.Ids;
    @Input() canEdit?: boolean = false;
    @Input() eventId?: number;
    @Input() occs: S25Reservation[];
    @Input() isSeparatedView?: boolean = false;
    @Input() selectedItems?: number[] = [];
    init: boolean = false;

    @Output() toggleDetailsRow: EventEmitter<Table.NewRowModel> = new EventEmitter<Table.NewRowModel>();
    @Output() refresh = new EventEmitter<void>();
    toggleShowDetails: boolean = false;

    readonly occurrence = computed(() => {
        return this.occurrencesService.getOccurrences().find((r) => r.itemId === this.occ?.itemId) ?? null;
    });

    isExpandAll = computed(() => this.occurrencesService.getExpandAll());

    readonly eventWaiting = computed(() => {
        const occ = this.occurrence();
        const entries = this.filterWaitlistEntriesByTimeOverlap(occ, occ?.waitlistEntries, false);
        return new Set(entries.map((entry) => entry.eventId).filter((eventId) => eventId != null)).size;
    });

    readonly waitlistRsrvWaiting = computed(() => {
        const occ = this.occurrence();
        return this.filterWaitlistEntriesByTimeOverlap(occ, occ?.waitlistRsrvEntries, true);
    });

    readonly locationWaiting = computed(
        () => new Set((this.waitlistRsrvWaiting() ?? []).map((e) => e.spaceId).filter((id) => id != null)).size,
    );

    constructor(
        private occurrencesService: S25EventOccurrencesService,
        private cd: ChangeDetectorRef,
    ) {}

    async ngOnInit() {
        this.init = true;
        this.cd.markForCheck();
    }

    showDetails(event: any, caller: "autoClick" | "manualClick") {
        const elements: any = this.isSeparatedView
            ? document.querySelectorAll(".occ-expand-all-" + this.occ?.profileId)
            : document.querySelectorAll(".toggle-details-button");
        const index = this.occs.findIndex((item) => item.itemId === this.occurrence().itemId);
        if (elements) elements[index].click();
    }

    refreshF() {
        this.refresh.emit(); // get refresh get the latest update info
    }

    onChangeActive(e: any) {
        S25ReservationUtil.setState(this.occurrence(), e.target.value);
    }

    /**
     * Filters and flattens waitlist entries to include only waiting entries that overlap
     * with the current occurrence's time range
     */
    private filterWaitlistEntriesByTimeOverlap(
        occ: S25Reservation | null,
        entries: WaitlistEntry[] | undefined,
        excludeRequests: boolean,
    ): WaitingEntry[] {
        if (!occ || !entries?.length) return [];

        const occStart = occ.eventStart;
        const occEnd = occ.eventEnd;

        let filteredEntries = entries;
        if (excludeRequests) {
            const requestedIds = new Set(occ.locations?.[0]?.requested?.map((loc) => loc.itemId));
            filteredEntries = entries.filter((e) => !requestedIds.has(e.spaceId));
        }

        if (!occStart || !occEnd) {
            return filteredEntries.flatMap((e) => {
                const waiting = Array.isArray(e.waiting) ? e.waiting : [];
                return waiting.map((w) => ({
                    ...w,
                    spaceId: e.spaceId,
                    waitlistId: e.waitlistId,
                    blockingRsrvId: e.blockingRsrvId,
                    blockingEventId: e.blockingEventId,
                    blockingEventName: e.blockingEventName,
                }));
            });
        }

        return filteredEntries.flatMap((e) => {
            const waiting = Array.isArray(e.waiting) ? e.waiting : [];
            return waiting
                .filter((w) => {
                    const waitStart = new Date(w.startDt);
                    const waitEnd = new Date(w.endDt);
                    return waitStart < occEnd && occStart < waitEnd;
                })
                .map((w) => ({
                    ...w,
                    spaceId: e.spaceId,
                    waitlistId: e.waitlistId,
                    blockingRsrvId: e.blockingRsrvId,
                    blockingEventId: e.blockingEventId,
                    blockingEventName: e.blockingEventName,
                }));
        });
    }
}
